import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { Kpi, DarkCard } from "./SuperadminLayout";
import { toast } from "sonner";

export default function SuperadminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/dashboard");
        setData(data);
      } catch (e) {
        toast.error("Chargement impossible");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <SuperadminLayout title="Vue d'ensemble"><p className="text-[#8494A8]">Chargement…</p></SuperadminLayout>;
  if (!data) return <SuperadminLayout title="Vue d'ensemble"><p className="text-[#E4572E]">Erreur de chargement</p></SuperadminLayout>;

  const mrr = (data.mrr_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  const maxSignups = Math.max(1, ...data.signups_timeline.map((s) => s.count));

  return (
    <SuperadminLayout title="Vue d'ensemble">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Kpi label="MRR estimé" value={mrr} sub="par mois (récurrent)" testid="kpi-mrr" />
        <Kpi label="Abonnements actifs" value={data.subscriptions.active} sub={`${data.subscriptions.trialing} en essai`} testid="kpi-active" />
        <Kpi label="Praticiens" value={data.users.admins} sub={`+${data.users.new_30d} nouveaux (30j)`} testid="kpi-admins" />
        <Kpi label="Organisations" value={data.organizations.total} sub={`${data.organizations.establishments} établissements`} testid="kpi-orgs" />
      </div>

      {/* LLM cost — Emergent LLM Key spend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Kpi
          label="Coût IA (mois en cours)"
          value={((data.llm_cost?.mtd_micro_usd || 0) / 1_000_000).toLocaleString("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: 2 })}
          sub={`${(data.llm_cost?.mtd_calls || 0).toLocaleString("fr-FR")} appels · voir détails`}
          testid="kpi-llm-mtd"
        />
        <Kpi
          label="Coût IA (année en cours)"
          value={((data.llm_cost?.ytd_micro_usd || 0) / 1_000_000).toLocaleString("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: 2 })}
          sub={`${(data.llm_cost?.ytd_calls || 0).toLocaleString("fr-FR")} appels au total`}
          testid="kpi-llm-ytd"
        />
      </div>

      {/* Activity */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Questionnaires (total)" value={data.activity.templates_total} testid="kpi-templates" />
        <Kpi label="Patients (total)" value={data.activity.patients_total} testid="kpi-patients" />
        <Kpi label="Envois (30j)" value={data.activity.assignments_30d} sub={`sur ${data.activity.assignments_total} au total`} testid="kpi-sends" />
        <Kpi label="Réponses (30j)" value={data.activity.responses_30d} sub={`sur ${data.activity.responses_total} au total`} testid="kpi-responses" />
      </div>

      {/* Signups timeline */}
      <DarkCard className="p-6 mb-6" data-testid="signups-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Inscriptions (30 derniers jours)</h2>
            <p className="text-sm text-[#8494A8]">{data.users.new_30d} nouveaux comptes</p>
          </div>
        </div>
        <div className="flex items-end gap-1 h-40">
          {data.signups_timeline.length === 0 ? (
            <p className="text-sm text-[#8494A8]">Aucune inscription sur la période</p>
          ) : (
            data.signups_timeline.map((s) => (
              <div key={s.day} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div
                  className="w-full bg-[#E4572E] rounded-t transition-all hover:bg-[#F27551]"
                  style={{ height: `${(s.count / maxSignups) * 100}%`, minHeight: s.count > 0 ? "4px" : "0" }}
                  title={`${s.day}: ${s.count}`}
                />
                <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 bg-black/80 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                  {s.day} : {s.count}
                </div>
              </div>
            ))
          )}
        </div>
      </DarkCard>

      {/* Subscription breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DarkCard className="p-6" data-testid="subs-breakdown">
          <h2 className="text-lg font-semibold text-white mb-4">Abonnements</h2>
          <div className="space-y-3">
            {[
              { label: "Actifs", val: data.subscriptions.active, color: "#3A8FA5" },
              { label: "En essai", val: data.subscriptions.trialing, color: "#B0C97F" },
              { label: "Paiement échoué", val: data.subscriptions.past_due, color: "#E4A72E" },
              { label: "Résiliés", val: data.subscriptions.canceled, color: "#E4572E" },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between">
                <span className="text-sm text-[#B7C4D6]">
                  <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: s.color }} />
                  {s.label}
                </span>
                <span className="text-white font-mono">{s.val}</span>
              </div>
            ))}
          </div>
        </DarkCard>
        <DarkCard className="p-6" data-testid="users-breakdown">
          <h2 className="text-lg font-semibold text-white mb-4">Utilisateurs par rôle</h2>
          <div className="space-y-3">
            {[
              { label: "Praticiens (admin/org_admin)", val: data.users.admins },
              { label: "Assistants", val: data.users.assistants },
              { label: "Recherche clinique", val: data.users.research },
              { label: "Comptes désactivés", val: data.users.disabled },
              { label: "Total", val: data.users.total, bold: true },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between">
                <span className={`text-sm ${s.bold ? "text-white font-semibold" : "text-[#B7C4D6]"}`}>{s.label}</span>
                <span className={`font-mono ${s.bold ? "text-white font-bold" : "text-white"}`}>{s.val}</span>
              </div>
            ))}
          </div>
        </DarkCard>
      </div>
      <p className="text-xs text-[#8494A8] mt-6 text-right">
        Mise à jour : {new Date(data.generated_at).toLocaleString("fr-FR")}
      </p>
    </SuperadminLayout>
  );
}
